import type {
  ConfirmarAcaoDto,
  ConfirmarAcaoResponse,
  CopilotoAcaoCartao,
  CopilotoAcaoCartaoComToken,
  CopilotoAnexoResponse,
  CopilotoConversaResponse,
  CopilotoConversaResumo,
  CopilotoEvento,
  CopilotoMensagemDto,
  CopilotoStatusResponse,
  CopilotoTelaResponse,
  CopilotoTranscricaoResponse,
} from '@triade/contracts';
import { ApiError, cabecalhosAutenticados, enviarArquivo, expirarSeSessao, montarUrl, normalizarErro, requisitar, safeJson } from '@/lib/api';
import { useConsulta, useMutacao } from './base';

/**
 * Copiloto (IA, somente leitura). O front manda SO a conversa e a tela (rota + entidade): nenhum numero sai daqui.
 * A resposta e um stream SSE; o texto chega como delta e e exibido como texto puro.
 */

/** GET /copiloto/status: { configurado, modelo }. Exige empresa (mesmas permissoes das demais rotas). */
export function useCopilotoStatus() {
  return useConsulta<CopilotoStatusResponse>(['copiloto', 'status'], '/copiloto/status');
}

export interface BlocoSse {
  event: string;
  data: string;
}

const TIPOS_EVENTO = ['conversa', 'titulo', 'texto', 'ferramenta', 'acao', 'navegar', 'fim', 'erro'] as const;

/** Um bloco SSE (linhas `event:` e `data:`, sem a linha em branco final). Linhas com ':' no inicio sao comentario. */
export function parsearBlocoSse(texto: string): BlocoSse | null {
  let event = 'message';
  const dados: string[] = [];
  for (const linha of texto.split(/\r?\n/)) {
    if (linha === '' || linha.startsWith(':')) continue;
    const dois = linha.indexOf(':');
    const campo = dois < 0 ? linha : linha.slice(0, dois);
    const valor = dois < 0 ? '' : linha.slice(dois + 1).replace(/^ /, '');
    if (campo === 'event') event = valor;
    else if (campo === 'data') dados.push(valor);
  }
  return dados.length === 0 ? null : { event, data: dados.join('\n') };
}

/** Bloco SSE -> evento tipado. O `event` define o tipo (fallback: campo `tipo` do JSON). Dado invalido vira null. */
export function blocoParaEvento(bloco: BlocoSse): CopilotoEvento | null {
  const json = safeJson(bloco.data);
  if (!json || typeof json !== 'object') return null;
  const tipo = (TIPOS_EVENTO as readonly string[]).includes(bloco.event) ? bloco.event : (json as { tipo?: unknown }).tipo;
  if (typeof tipo !== 'string' || !(TIPOS_EVENTO as readonly string[]).includes(tipo)) return null;
  return { ...(json as object), tipo } as CopilotoEvento;
}

/** Le o ReadableStream do SSE: os blocos sao separados por linha em branco (LF ou CRLF); o resto fica para o proximo pedaco. */
export async function lerSse(corpo: ReadableStream<Uint8Array>, aoBloco: (bloco: BlocoSse) => void): Promise<void> {
  const leitor = corpo.getReader();
  const decodificador = new TextDecoder();
  let pendente = '';
  for (;;) {
    const { done, value } = await leitor.read();
    if (done) break;
    pendente += decodificador.decode(value, { stream: true });
    const partes = pendente.split(/\r?\n\r?\n/);
    pendente = partes.pop() ?? '';
    for (const parte of partes) {
      const bloco = parsearBlocoSse(parte);
      if (bloco) aoBloco(bloco);
    }
  }
  pendente += decodificador.decode();
  const final = pendente.trim() ? parsearBlocoSse(pendente) : null;
  if (final) aoBloco(final);
}

/**
 * POST /copiloto/mensagens. Erros antes do stream (503 COPILOTO_NAO_CONFIGURADO, 429 COPILOTO_LIMITE) viram ApiError
 * com code/message. Abortavel por `sinal` (botao Parar): o fetch rejeita com AbortError.
 */
export async function enviarParaCopiloto(
  corpo: CopilotoMensagemDto,
  opts: { onEvento: (evento: CopilotoEvento) => void; sinal?: AbortSignal },
): Promise<void> {
  const resp = await fetch(montarUrl('/copiloto/mensagens'), {
    method: 'POST',
    headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json', ...cabecalhosAutenticados() },
    body: JSON.stringify(corpo),
    signal: opts.sinal,
  });
  if (!resp.ok) {
    const texto = await resp.text();
    const json = texto ? safeJson(texto) : null;
    expirarSeSessao(resp.status, json);
    throw new ApiError(resp.status, normalizarErro(resp.status, json));
  }
  if (!resp.body) throw new ApiError(resp.status, normalizarErro(resp.status, null));
  await lerSse(resp.body, (bloco) => {
    const evento = blocoParaEvento(bloco);
    if (evento) opts.onEvento(evento);
  });
}

/** Chave de todas as listas de conversas (invalidada ao renomear, excluir e ao receber `conversa`/`titulo` no SSE). */
export const CHAVE_CONVERSAS = ['copiloto', 'conversas'] as const;

/** GET /copiloto/conversas?q= (busca pelo titulo; vazio = todas). */
export function useConversas(q: string) {
  const busca = q.trim();
  return useConsulta<CopilotoConversaResumo[]>([...CHAVE_CONVERSAS], '/copiloto/conversas', {
    query: busca ? { q: busca } : {},
  });
}

/** GET /copiloto/conversas/:id com as mensagens (Retomar). Sem cache: sempre o estado do banco. */
export function buscarConversa(id: string): Promise<CopilotoConversaResponse> {
  return requisitar<CopilotoConversaResponse>(`/copiloto/conversas/${encodeURIComponent(id)}`);
}

/** PATCH /copiloto/conversas/:id { titulo }. */
export function useRenomearConversa() {
  return useMutacao<CopilotoConversaResumo, { id: string; titulo: string }>({
    metodo: 'PATCH',
    caminho: ({ id }) => `/copiloto/conversas/${encodeURIComponent(id)}`,
    corpo: ({ titulo }) => ({ titulo }),
    invalidar: [[...CHAVE_CONVERSAS]],
  });
}

/** DELETE /copiloto/conversas/:id (204). */
export function useExcluirConversa() {
  return useMutacao<void, { id: string }>({
    metodo: 'DELETE',
    caminho: ({ id }) => `/copiloto/conversas/${encodeURIComponent(id)}`,
    invalidar: [[...CHAVE_CONVERSAS]],
  });
}

/** POST /copiloto/anexos (multipart, campo `arquivo`, um por requisicao). Erros viram ApiError com code. */
export function enviarAnexo(arquivo: File): Promise<CopilotoAnexoResponse> {
  return enviarArquivo<CopilotoAnexoResponse>('/copiloto/anexos', arquivo, arquivo.name, 'POST');
}

/** DELETE /copiloto/anexos/:id (so anexo pendente; 204). */
export function removerAnexo(id: string): Promise<void> {
  return requisitar<void>(`/copiloto/anexos/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** POST /copiloto/transcricoes (multipart, campo `audio`) -> { texto }. 503 TRANSCRICAO_NAO_CONFIGURADA vira ApiError. */
export async function transcreverAudio(audio: Blob, nome = 'gravacao.webm'): Promise<CopilotoTranscricaoResponse> {
  const corpo = new FormData();
  corpo.append('audio', audio, nome);
  const resp = await fetch(montarUrl('/copiloto/transcricoes'), {
    method: 'POST',
    headers: { Accept: 'application/json', ...cabecalhosAutenticados() },
    body: corpo,
  });
  const texto = await resp.text();
  const json: unknown = texto ? safeJson(texto) : null;
  expirarSeSessao(resp.status, json);
  if (!resp.ok) throw new ApiError(resp.status, normalizarErro(resp.status, json));
  return json as CopilotoTranscricaoResponse;
}

/** POST /copiloto/acoes/:id/confirmar: executa a acao (token de uso unico; SENSIVEL exige reforcada + palavra). */
export function confirmarAcao(id: string, corpo: ConfirmarAcaoDto): Promise<ConfirmarAcaoResponse> {
  return requisitar<ConfirmarAcaoResponse>(`/copiloto/acoes/${encodeURIComponent(id)}/confirmar`, { method: 'POST', body: corpo });
}

/** POST /copiloto/acoes/:id/cancelar: cartao vira CANCELADA (sem token). */
export function cancelarAcao(id: string): Promise<CopilotoAcaoCartao> {
  return requisitar<CopilotoAcaoCartao>(`/copiloto/acoes/${encodeURIComponent(id)}/cancelar`, { method: 'POST', body: {} });
}

/** POST /copiloto/acoes/:id/previa: recalcula a previa e devolve um token novo (depois de recarregar, expirar ou 409 CONTEUDO_ALTERADO). */
export function repreviaAcao(id: string): Promise<CopilotoAcaoCartaoComToken> {
  return requisitar<CopilotoAcaoCartaoComToken>(`/copiloto/acoes/${encodeURIComponent(id)}/previa`, { method: 'POST', body: {} });
}

/** GET /copiloto/tela?rota=: nome da tela e perguntas sugeridas (registro de manuais). Erro/ausencia: o chat usa as locais. */
export function useTelaCopiloto(rota: string) {
  return useConsulta<CopilotoTelaResponse>(['copiloto', 'tela', rota], '/copiloto/tela', { query: { rota } });
}

/**
 * PUT /copiloto/acoes/config { ligado } -> { acoes }: liga/desliga as acoes nesta empresa (exige parametros.editar; 409
 * ACOES_INDISPONIVEIS se a env estiver off). O flag tambem vem em /me/empresas, por isso as duas chaves sao invalidadas.
 */
export function useDefinirAcoesCopiloto() {
  return useMutacao<{ acoes: boolean }, { ligado: boolean }>({
    metodo: 'PUT',
    caminho: () => '/copiloto/acoes/config',
    corpo: ({ ligado }) => ({ ligado }),
    invalidar: [['copiloto', 'status'], ['me', 'empresas']],
  });
}
